import { useEffect, useRef, useState } from "react";
import { X, Check, Star, MapPin, Navigation, Wallet } from "lucide-react";
import api, { resolveImageUrl } from "../lib/api";
import { toast } from "sonner";

const DEFAULT_COUNTDOWN_SECONDS = 70;

function fmtXAF(n) { return `${Number(n || 0).toLocaleString("fr-FR").replace(/,/g, " ")} F`; }

/**
 * Full-screen "incoming call" modal shown to a driver when a new Express
 * course is offered. Auto-declines after the admin-configured countdown
 * (default 70 s, read live from the offer's `accept_timeout_seconds` field
 * so the backend and modal stay in sync) so the offer can then be routed
 * to the next-nearest driver by `_reassign_express_booking()`.
 */
export default function ExpressIncomingCallModal({ open, offer, onAccepted, onDeclined, onClose }) {
  // The offer payload embeds `accept_timeout_seconds` (from the admin's
  // Express config). Falling back to 70 keeps behaviour sane even when
  // an older backend forgot to include the field.
  const countdownSeconds = Math.max(15, Math.min(300, Number(offer?.accept_timeout_seconds) || DEFAULT_COUNTDOWN_SECONDS));
  const [remaining, setRemaining] = useState(countdownSeconds);
  const [busy, setBusy] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setRemaining(countdownSeconds);
    intervalRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(intervalRef.current);
          // Auto-decline
          _autoDecline();
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => intervalRef.current && clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, offer?.booking_id, countdownSeconds]);

  const _autoDecline = async () => {
    if (busy || !offer?.booking_id) return;
    setBusy(true);
    try {
      // Use the Express-specific decline endpoint so the backend re-routes
      // this offer to the next nearest driver.
      await api.post(`/express/${offer.booking_id}/decline`);
    } catch { /* ignore */ }
    finally {
      setBusy(false);
      onDeclined?.();
      onClose?.();
    }
  };

  const accept = async () => {
    if (busy || !offer?.booking_id) return;
    setBusy(true);
    // Stop ringtone immediately
    if (typeof window !== "undefined") {
      window.__trigoStopBookingRingtone?.();
      window.__trigoAcknowledgeBookingAlert?.(offer.booking_id);
    }
    try {
      await api.post(`/express/${offer.booking_id}/accept`);
      // Auto-start GPS tracking so the passenger sees the driver approaching
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => api.post("/location/update", { lat: pos.coords.latitude, lng: pos.coords.longitude, booking_id: offer.booking_id }).catch(() => {}),
          () => {},
          { enableHighAccuracy: true, timeout: 6000 }
        );
      }
      // Flag global tracking + redirect the driver to the Courses Express tab
      window.dispatchEvent(new CustomEvent("trigo:start-tracking", { detail: { bookingId: offer.booking_id } }));
      window.dispatchEvent(new CustomEvent("trigo:express-accepted", { detail: { bookingId: offer.booking_id } }));
      onAccepted?.(offer);
      toast.success("Course acceptée ✓");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
      onClose?.();
    }
  };

  const decline = async () => {
    if (busy || !offer?.booking_id) return;
    setBusy(true);
    if (typeof window !== "undefined") {
      window.__trigoStopBookingRingtone?.();
      window.__trigoAcknowledgeBookingAlert?.(offer.booking_id);
    }
    try {
      await api.post(`/express/${offer.booking_id}/decline`);
      onDeclined?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
      onClose?.();
    }
  };

  if (!open || !offer) return null;

  const pct = (remaining / countdownSeconds) * 100;
  const initials = (offer.user_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const gain = Math.round((offer.total_amount || 0) * 0.85); // 85% net (15% TFT commission)

  return (
    <div
      className="fixed inset-0 z-[80] flex flex-col text-white"
      style={{ background: "radial-gradient(120% 100% at 50% 0%, #1B3D5E 0%, #0F2B46 55%, #0A1E33 100%)" }}
      data-testid="express-incoming-call"
      role="dialog"
      aria-modal="true"
    >
      {/* Pulsing rings + avatar */}
      <div className="flex items-center justify-center pt-14 relative" style={{ height: "180px" }}>
        <span className="absolute rounded-full border-2 border-[#F2A93B]/50" style={{ width: 110, height: 110, animation: "trigoRing 1.8s infinite" }} />
        <span className="absolute rounded-full border-2 border-[#F2A93B]/50" style={{ width: 110, height: 110, animation: "trigoRing 1.8s infinite", animationDelay: "0.6s" }} />
        <span className="absolute rounded-full border-2 border-[#F2A93B]/50" style={{ width: 110, height: 110, animation: "trigoRing 1.8s infinite", animationDelay: "1.2s" }} />
        {offer.user_avatar ? (
          <img src={resolveImageUrl(offer.user_avatar)} alt="" className="relative z-10 w-24 h-24 rounded-full object-cover border-4 border-white/25" />
        ) : (
          <div className="relative z-10 w-24 h-24 rounded-full bg-[#F2A93B] text-[#0F2B46] font-extrabold text-3xl flex items-center justify-center border-4 border-white/25" style={{ fontFamily: "Sora" }}>
            {initials}
          </div>
        )}
      </div>

      {/* Caller info */}
      <div className="text-center mt-3">
        <div className="text-[11px] font-bold uppercase tracking-widest text-[#F2A93B]" style={{ fontFamily: "Sora" }}>Nouvelle course Express</div>
        <div className="text-xl font-extrabold mt-0.5" style={{ fontFamily: "Sora" }} data-testid="express-call-name">{offer.user_name || "Passager"}</div>
        {offer.user_rating > 0 && (
          <div className="text-xs text-[#B9CBDA] mt-0.5 flex items-center justify-center gap-1">
            <Star className="w-3 h-3 text-[#F2A93B]" fill="#F2A93B" /> {offer.user_rating.toFixed(1)}
          </div>
        )}
      </div>

      {/* Countdown */}
      <div className="flex items-center justify-center gap-2 mt-3">
        <div className="w-36 h-[5px] rounded bg-white/15 overflow-hidden">
          <div className="h-full bg-[#F2A93B] transition-all duration-[900ms] ease-linear" style={{ width: `${pct}%` }} data-testid="express-call-countdown-bar" />
        </div>
        <span className="text-xs font-extrabold text-[#F2A93B] tabular-nums" style={{ fontFamily: "Sora" }} data-testid="express-call-countdown">{`${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, "0")}`}</span>
      </div>

      {/* Trip card */}
      <div className="mx-5 mt-5 rounded-2xl p-4 border border-white/12 backdrop-blur" style={{ background: "rgba(255,255,255,0.08)" }}>
        <div className="flex items-start gap-2.5">
          <span className="w-2.5 h-2.5 rounded-full bg-[#3FAE7A] mt-1.5" />
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-widest text-[#B9CBDA]">Prise en charge</div>
            <div className="text-sm font-semibold truncate">{offer.departure}</div>
          </div>
        </div>
        <div className="ml-1 my-1 w-[2px] h-4 bg-white/25" />
        <div className="flex items-start gap-2.5">
          <span className="w-2.5 h-2.5 rounded-full bg-[#D98C1F] mt-1.5" />
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-widest text-[#B9CBDA]">Destination</div>
            <div className="text-sm font-semibold truncate">{offer.destination}</div>
          </div>
        </div>

        <div className="flex justify-between mt-3 pt-3 border-t border-dashed border-white/18">
          <div className="text-center flex-1">
            <div className="text-base font-extrabold text-[#F2A93B]" style={{ fontFamily: "Sora" }}>{(offer.driver_distance_km || 0).toFixed(1)} km</div>
            <div className="text-[10.5px] text-[#B9CBDA] flex items-center justify-center gap-1"><Navigation className="w-2.5 h-2.5" /> jusqu&apos;au client</div>
          </div>
          <div className="text-center flex-1 border-l border-white/15">
            <div className="text-base font-extrabold text-[#F2A93B]" style={{ fontFamily: "Sora" }}>{(offer.estimated_distance_km || 0).toFixed(1)} km</div>
            <div className="text-[10.5px] text-[#B9CBDA] flex items-center justify-center gap-1"><MapPin className="w-2.5 h-2.5" /> course totale</div>
          </div>
          <div className="text-center flex-1 border-l border-white/15">
            <div className="text-base font-extrabold text-[#F2A93B]" style={{ fontFamily: "Sora" }} data-testid="express-call-gain">{fmtXAF(gain)}</div>
            <div className="text-[10.5px] text-[#B9CBDA] flex items-center justify-center gap-1"><Wallet className="w-2.5 h-2.5" /> votre gain net</div>
          </div>
        </div>
      </div>

      <div className="flex-1" />

      {/* Buttons */}
      <div className="flex items-start justify-between px-8 pb-10 pt-4">
        <div className="flex flex-col items-center">
          <button
            onClick={decline}
            disabled={busy}
            className="w-[76px] h-[76px] rounded-full bg-[#C1453B] text-white flex items-center justify-center shadow-[0_10px_24px_rgba(0,0,0,0.35)] active:scale-95 disabled:opacity-60 transition-transform"
            aria-label="Refuser la course"
            data-testid="express-call-decline"
          >
            <X className="w-8 h-8" />
          </button>
          <span className="text-[11px] text-[#B9CBDA] mt-2 font-semibold">Refuser</span>
        </div>
        <div className="flex flex-col items-center">
          <button
            onClick={accept}
            disabled={busy}
            className="w-[76px] h-[76px] rounded-full bg-[#3FAE7A] text-white flex items-center justify-center shadow-[0_10px_24px_rgba(0,0,0,0.35)] active:scale-95 disabled:opacity-60 transition-transform"
            style={{ animation: "trigoBob 1.4s ease-in-out infinite" }}
            aria-label="Accepter la course"
            data-testid="express-call-accept"
          >
            <Check className="w-8 h-8" />
          </button>
          <span className="text-[11px] text-[#B9CBDA] mt-2 font-semibold">Accepter</span>
        </div>
      </div>

      {/* Local keyframes */}
      <style>{`
        @keyframes trigoRing {
          0%   { width: 110px; height: 110px; opacity: 0.9; }
          100% { width: 220px; height: 220px; opacity: 0; }
        }
        @keyframes trigoBob {
          0%,100% { transform: translateY(0); }
          50%     { transform: translateY(-6px); }
        }
      `}</style>
    </div>
  );
}
